import { Link as LinkS } from "react-scroll";
import styled from "styled-components";
import { COLORS } from "../../../const/colors";

export const HeroSectionMain = styled.section`
  padding: 5.5rem 0 1rem;
  position: relative;
  overflow: hidden;
  z-index: 1;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      ${COLORS.LIGHT_GRAY_80} 0%,
      ${COLORS.WHITE_90} 100%
    );
    z-index: -1;
  }

  &::after {
    content: "";
    position: absolute;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      ${COLORS.ORANGE_10} 0%,
      rgba(255, 255, 255, 0) 70%
    );
    top: 20%;
    right: -150px;
    z-index: -1;
  }
`;

export const HeroContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: grid;
  gap: 1.25rem;
  position: relative;
  row-gap: 1.5rem;
  padding-top: 2rem;
  z-index: 2;

  @media screen and (min-width: 992px) {
    padding-top: 4rem;
  }

  @media screen and (max-width: 768px) {
    padding: 0 1rem;
    row-gap: 1rem;
  }
`;

export const HeroData = styled.div`
  text-align: center;
  position: relative;
  z-index: 10;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media screen and (min-width: 768px) {
    text-align: left;
    align-items: flex-start;
  }

  @media screen and (max-width: 480px) {
    padding: 0 0.5rem;
  }
`;

export const HeroGreeting = styled.span`
  display: inline-block;
  color: ${(props) => (props.light ? COLORS.WHITE : COLORS.ORANGE)};
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${(props) => (props.light ? COLORS.WHITE_10 : COLORS.ORANGE_10)};
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid
    ${(props) => (props.light ? COLORS.WHITE_20 : COLORS.ORANGE_20)};
  transition: all 0.3s ease;
  align-self: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  @media screen and (min-width: 768px) {
    align-self: flex-start;
  }

  @media screen and (max-width: 480px) {
    font-size: 0.75rem;
    padding: 0.4rem 0.8rem;
  }
`;

export const HeroName = styled.h1`
  font-size: clamp(2.5rem, 6vw, 3.5rem);
  color: ${(props) => (props.light ? COLORS.WHITE : COLORS.ORANGE)};
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: -1px;
  font-weight: 800;
  background: ${(props) =>
    props.light
      ? `linear-gradient(to right, ${COLORS.LIGHT_GRAY}, ${COLORS.WHITE})`
      : `linear-gradient(to right, ${COLORS.ORANGE}, ${COLORS.DARK_BLUE})`};
  -webkit-background-clip: ${(props) => (props.light ? "none" : "text")};
  -webkit-text-fill-color: ${(props) =>
    props.light ? "inherit" : "transparent"};
  margin-bottom: 0.5rem;
  position: relative;
  text-align: center;
  width: 100%;

  &::after {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%);
    width: 50%;
    height: 3px;
    background: linear-gradient(
      to right,
      ${COLORS.ORANGE},
      ${COLORS.ORANGE_50}
    );
    border-radius: 3px;

    @media screen and (min-width: 768px) {
      left: 0;
      transform: none;
      width: 40%;
    }

    @media screen and (max-width: 480px) {
      width: 70%;
    }
  }

  @media screen and (min-width: 768px) {
    text-align: left;
    width: 100%;
  }

  @media screen and (max-width: 480px) {
    font-size: clamp(2rem, 5vw, 2.5rem);
    letter-spacing: -0.5px;
    margin-bottom: 0.75rem;
  }
`;

export const HeroEducation = styled.h3`
  color: ${(props) => (props.light ? COLORS.LIGHT_GRAY : COLORS.DARK_BLUE)};
  margin-bottom: 1.5rem;
  font-weight: 500;
  font-size: 1.1rem;
  letter-spacing: 0.5px;
  text-align: center;
  width: 100%;

  @media screen and (min-width: 768px) {
    font-size: 1.25rem;
    text-align: left;
    width: 100%;
  }

  @media screen and (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
`;

export const HeroButtons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  width: 100%;
  color: ${COLORS.ORANGE};

  @media screen and (min-width: 768px) {
    justify-content: flex-start;
    width: 100%;
  }

  @media screen and (max-width: 500px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

export const HeroImg = styled.img`
  width: 220px;
  height: 220px;
  justify-self: center;
  background-position: center center;
  margin: 20px auto;
  border: 4px solid ${COLORS.ORANGE_20};
  position: relative;
  z-index: 10;

  /* Enhanced shadow effect */
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15), 0 5px 15px ${COLORS.ORANGE_10},
    inset 0 0 15px rgba(255, 255, 255, 0.5);

  /* Improved animation */
  animation: profile-animate 8s ease-in-out infinite;
  transition: all 0.5s ease-in-out;

  /* Shimmer effect */
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: -10px;
    background: linear-gradient(
      90deg,
      ${COLORS.ORANGE} 0%,
      ${COLORS.DARK_BLUE} 50%,
      ${COLORS.ORANGE} 100%
    );
    z-index: -1;
    border-radius: inherit;
    animation: rotate 4s linear infinite;
  }

  &::after {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      to bottom right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: rotate(30deg);
    animation: shimmer 5s linear infinite;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes shimmer {
    0% {
      transform: rotate(30deg) translateX(-100%);
    }
    100% {
      transform: rotate(30deg) translateX(100%);
    }
  }

  @keyframes profile-animate {
    0%,
    100% {
      border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
    }
    25% {
      border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%;
    }
    50% {
      border-radius: 55% 45% 65% 35% / 40% 50% 50% 60%;
    }
    75% {
      border-radius: 35% 65% 40% 60% / 60% 35% 65% 40%;
    }
  }

  /* Improved responsiveness */
  @media screen and (max-width: 320px) {
    width: 160px;
    height: 160px;
  }

  @media screen and (max-width: 480px) {
    width: 180px;
    height: 180px;
    margin: 10px auto;
  }

  @media screen and (min-width: 992px) {
    width: 260px;
    height: 260px;
    margin-bottom: 2rem;

    &:hover {
      transform: scale(1.05) rotate(5deg);
      box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2),
        0 10px 20px ${COLORS.ORANGE_15}, inset 0 0 15px rgba(255, 255, 255, 0.7);
    }
  }
`;

export const BlobImg = styled.img`
  width: 200px;
  position: relative;
  z-index: 10;

  @media screen and (max-width: 320px) {
    width: 130px;
  }

  @media screen and (min-width: 992px) {
    width: 220px;
  }
`;

export const HeroSocial = styled.div`
  position: absolute;
  bottom: 5rem;
  left: 0;
  display: grid;
  row-gap: 0.75rem;
  z-index: 20;

  &::after {
    content: "";
    width: 32px;
    height: 2px;
    background-color: ${COLORS.ORANGE};
    transform: rotate(90deg) translate(16px, 3px);
  }

  @media screen and (min-width: 992px) {
    &::after {
      transform: rotate(90deg) translate(16px, 0);
    }
  }

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

export const HeroSocialLink = styled.a`
  width: max-content;
  background-color: ${COLORS.DARK_BLUE};
  color: ${COLORS.ORANGE};
  padding: 0.5rem;
  border-radius: 0.5rem;
  display: flex;
  font-size: 1.25rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 20;

  &:hover {
    background-color: ${COLORS.ORANGE};
    color: ${COLORS.WHITE};
    transform: translateY(-5px) scale(1.1);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  }

  @media screen and (min-width: 992px) {
    padding: 0.5rem;
    font-size: 1.5rem;
  }
`;

export const HeroScroll = styled(LinkS)`
  cursor: pointer;
  position: absolute;
  color: ${COLORS.ORANGE};
  right: -1.5rem;
  bottom: 5rem;
  display: grid;
  row-gap: 2.25rem;
  justify-items: center;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  z-index: 20;

  &:hover {
    transform: translateY(5px);
    color: ${COLORS.DARK_BLUE};
  }

  @media screen and (min-width: 992px) {
    font-size: 2rem;
    right: 0;
    bottom: 5rem;
  }

  @media screen and (max-width: 768px) {
    right: 1rem;
    bottom: 2rem;
  }

  @media screen and (max-width: 480px) {
    font-size: 1.25rem;
    bottom: 4rem;
    right: 0.5rem;
  }
`;

export const HeroScrollName = styled.span`
  font-size: 0.75rem;
  transform: rotate(-90deg);
  font-weight: 500;
  letter-spacing: 0.1rem;

  @media screen and (max-width: 480px) {
    font-size: 0.7rem;
    letter-spacing: 0.05rem;
  }
`;

export const ButtonCV = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${COLORS.ORANGE};
  color: ${COLORS.WHITE};
  padding: 0.85rem 1.5rem;
  border-radius: 0.75rem;
  font-weight: 500;
  font-size: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 4px 10px ${COLORS.ORANGE_30};
  position: relative;
  overflow: hidden;
  z-index: 10;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      ${COLORS.WHITE_30},
      transparent
    );
    transition: all 0.6s ease;
  }

  &:hover {
    background-color: ${COLORS.DARK_BLUE};
    transform: translateY(-3px);
    box-shadow: 0 6px 15px ${COLORS.DARK_BLUE_30};

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px ${COLORS.ORANGE_30};
  }

  &.hero_ghost {
    background-color: transparent;
    border: 2px solid ${COLORS.ORANGE};
    color: ${COLORS.ORANGE};
    box-shadow: 0 4px 8px ${COLORS.ORANGE_20};

    &:hover {
      background-color: ${COLORS.ORANGE_10};
      color: ${COLORS.DARK_BLUE};
      border-color: ${COLORS.DARK_BLUE};
    }
  }

  &:disabled {
    background-color: ${COLORS.LIGHT_GRAY};
    cursor: not-allowed;
    box-shadow: none;

    &:hover {
      transform: none;
    }
  }

  @media screen and (max-width: 480px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.9rem;
  }
`;
